import React, { useState, useEffect, useRef, useMemo } from "react";
import { Helmet } from "react-helmet";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Clock, Calendar, ArrowLeft, Share2, ChevronRight, User, HelpCircle, List, ArrowUp, Copy, Check, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { API, setContentSource } from "@/lib/constants";
import { utmPayload } from "@/lib/utm";
import { getHutk } from "@/lib/hubspot";
import PageMeta from "@/components/PageMeta";
import TexasCoverageWidget from "@/components/TexasCoverageWidget";
import SwipeFileGate from "@/components/SwipeFileGate";

const DEFAULT_IMAGE = "https://customer-assets.emergentagent.com/job_e227742f-fa2d-44b4-a974-4045826f8a41/artifacts/epvouypm_DMN%20Logo%20Final%20A%20%28Light%29%20transparent.PNG";

// Internal link mapping for SEO
const SITE_LINKS = [
  { text: "local SEO", url: "/services/local-seo" },
  { text: "Local SEO", url: "/services/local-seo" },
  { text: "SEO services", url: "/services/seo" },
  { text: "SEO strategy", url: "/blog/seo-complete-guide" },
  { text: "Google Business Profile", url: "/blog/google-business-profile-guide" },
  { text: "GBP optimization", url: "/services/gbp-optimization" },
  { text: "content marketing", url: "/services/content-marketing" },
  { text: "Content Marketing", url: "/blog/content-marketing-strategy-guide" },
  { text: "PPC", url: "/services/ppc" },
  { text: "Google Ads", url: "/blog/google-ads-guide" },
  { text: "social media marketing", url: "/blog/social-media-marketing-guide" },
  { text: "social media", url: "/services/social-media" },
  { text: "email marketing", url: "/blog/email-marketing-guide" },
  { text: "web design", url: "/services/web-design" },
  { text: "link building", url: "/blog/link-building-guide" },
  { text: "keyword research", url: "/blog/keyword-research-guide" },
  { text: "conversion rate optimization", url: "/blog/conversion-rate-optimization-guide" },
  { text: "video marketing", url: "/blog/video-marketing-guide" },
  { text: "reputation management", url: "/services/reputation-management" },
  { text: "AEO", url: "/what-is-aeo" },
  { text: "Answer Engine Optimization", url: "/what-is-aeo" },
  { text: "GEO", url: "/what-is-geo" },
  { text: "Generative Engine Optimization", url: "/what-is-geo" },
  { text: "LLM optimization", url: "/what-is-llmo" },
  { text: "AI optimization", url: "/services/geo" },
  { text: "landing page", url: "/blog/landing-page-optimization-guide" },
  { text: "voice search", url: "/blog/voice-search-optimization-guide" },
  { text: "Shopify SEO", url: "/blog/shopify-seo-guide" },
  { text: "YouTube SEO", url: "/blog/youtube-seo" },
  { text: "Pinterest marketing", url: "/blog/pinterest-marketing-guide" },
  { text: "TikTok", url: "/blog/how-to-become-tiktok-famous" },
  { text: "LinkedIn marketing", url: "/blog/linkedin-marketing-strategy" },
  { text: "Instagram marketing", url: "/blog/instagram-marketing-strategy" },
  { text: "Facebook advertising", url: "/blog/facebook-advertising-guide" },
  { text: "website traffic", url: "/blog/how-to-increase-website-traffic" },
  { text: "rank higher on Google", url: "/blog/how-to-rank-higher-on-google" },
  { text: "AI tools", url: "/blog/best-ai-tools" },
  { text: "digital marketing", url: "/services" },
  { text: "Dallas", url: "/contact" },
  { text: "case studies", url: "/case-studies" },
  { text: "multicultural marketing", url: "/services/multicultural-marketing" },
  { text: "Hispanic marketing", url: "/blog/hispanic-marketing" },
  { text: "bilingual marketing", url: "/blog/hispanic-marketing" },
  { text: "Hispanic consumers", url: "/blog/hispanic-marketing" },
];

// Reading progress bar with live time remaining
const WORDS_PER_MINUTE = 230;

const ReadingProgress = ({ wordCount }) => {
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);

  const totalMinutes = Math.max(1, Math.ceil((wordCount || 0) / WORDS_PER_MINUTE));

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      const pct = h > 0 ? Math.min((window.scrollY / h) * 100, 100) : 0;
      setProgress(pct);
      setVisible(window.scrollY > 300);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const minutesLeft = Math.max(0, Math.ceil(totalMinutes * (1 - progress / 100)));

  return (
    <>
      {/* Top progress bar */}
      <div className="fixed top-0 left-0 w-full h-1 z-[60] bg-transparent">
        <div
          className="h-full bg-gradient-to-r from-[#00E676] to-[#00C853] transition-[width] duration-150"
          style={{ width: `${progress}%` }}
        />
      </div>
      {/* Floating time-remaining pill */}
      {visible && wordCount > 0 && (
        <div
          className={`fixed top-4 right-4 z-[59] flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-md border border-white/10 text-xs transition-opacity duration-300 ${progress >= 98 ? 'opacity-0' : 'opacity-100'}`}
          data-testid="reading-time-remaining"
        >
          <Clock className="w-3 h-3 text-[#00E676]" />
          <span className="text-gray-300">
            {minutesLeft === 0 ? "Done!" : `${minutesLeft} min left`}
          </span>
        </div>
      )}
    </>
  );
};

// Extract headings from HTML content for Table of Contents
const extractHeadings = (html) => {
  if (!html) return [];
  const headingRegex = /<h([23])[^>]*>(.*?)<\/h[23]>/gi;
  const headings = [];
  const seen = new Set();
  let match;
  while ((match = headingRegex.exec(html)) !== null) {
    const text = match[2].replace(/<[^>]*>/g, "").trim();
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    if (seen.has(id)) continue;
    seen.add(id);
    headings.push({ level: parseInt(match[1]), text, id });
  }
  return headings;
};

// Extract headings from markdown
const extractMarkdownHeadings = (content) => {
  if (!content) return [];
  const lines = content.split("\n");
  const headings = [];
  const seen = new Set();
  lines.forEach((line) => {
    const trimmed = line.trim();
    if (trimmed.startsWith("## ") && !trimmed.startsWith("### ")) {
      const text = trimmed.replace("## ", "");
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
      if (!seen.has(id)) { seen.add(id); headings.push({ level: 2, text, id }); }
    } else if (trimmed.startsWith("### ")) {
      const text = trimmed.replace("### ", "");
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
      if (!seen.has(id)) { seen.add(id); headings.push({ level: 3, text, id }); }
    }
  });
  return headings;
};

// Table of Contents sidebar
const TableOfContents = ({ headings, activeId }) => {
  if (headings.length < 3) return null;

  return (
    <nav className="hidden xl:block sticky top-28 w-64 shrink-0" data-testid="table-of-contents">
      <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-white/[0.06]">
          <List className="w-4 h-4 text-[#00E676]" />
          <span className="text-sm font-semibold text-white">On This Page</span>
        </div>
        <ul className="space-y-1 max-h-[60vh] overflow-y-auto pr-1">
          {headings.map((h) => (
            <li key={h.id}>
              <a
                href={`#${h.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(h.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className={`block text-sm py-1.5 transition-colors duration-200 ${
                  h.level === 3 ? "pl-4" : "pl-0"
                } ${
                  activeId === h.id
                    ? "text-[#00E676] font-medium"
                    : "text-gray-500 hover:text-gray-300"
                }`}
              >
                {h.text}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

// Back to top button
const BackToTop = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!show) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="fixed bottom-20 right-8 z-50 w-10 h-10 bg-[#00E676] text-black rounded-full flex items-center justify-center shadow-lg hover:bg-[#00C853] transition-colors"
      data-testid="back-to-top-btn"
    >
      <ArrowUp className="w-5 h-5" />
    </button>
  );
};

const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeHeading, setActiveHeading] = useState("");
  const [copied, setCopied] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/posts/${slug}`);
        setPost(response.data);
        document.title = response.data.meta_title || response.data.title;
        
        const allPosts = await axios.get(`${API}/blog/posts`);
        const others = allPosts.data.filter(p => p.slug !== slug);
        const sameCategory = others.filter(p => p.category === response.data.category);
        const diffCategory = others.filter(p => p.category !== response.data.category);
        const related = [...sameCategory.slice(0, 3)];
        if (related.length < 3) {
          related.push(...diffCategory.slice(0, 3 - related.length));
        }
        setRelatedPosts(related);
      } catch (error) {
        console.error("Error fetching post:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
    window.scrollTo(0, 0);
  }, [slug]);

  // Track AI referral citations (ChatGPT, Perplexity, Claude, etc.)
  useEffect(() => {
    if (slug) setContentSource(`blog:${slug}`);
    const ref = document.referrer || "";
    if (slug && ref) {
      axios.post(`${API}/citations/track`, { slug, referrer: ref }).catch(() => {});
    }
  }, [slug]);

  // Track active heading for ToC
  useEffect(() => {
    if (!post) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveHeading(entry.target.id);
        });
      },
      { rootMargin: "-80px 0px -70% 0px", threshold: 0.1 }
    );
    const headingEls = contentRef.current?.querySelectorAll("h2[id], h3[id]");
    headingEls?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [post, loading]);

  // Execute embedded interactive-module scripts + wire article lead forms
  useEffect(() => {
    if (loading || !post || !contentRef.current) return;
    const container = contentRef.current;
    const runScripts = () => {
      container.querySelectorAll("script").forEach((el) => {
        if (el.type && el.type !== "text/javascript") return;
        if (el.dataset.dmnExecuted) return;
        el.dataset.dmnExecuted = "1";
        try {
          new Function(el.textContent)();
        } catch (err) {
          console.error("Article module script error:", err);
        }
      });
    };
    runScripts();
    const mo = new MutationObserver(runScripts);
    mo.observe(container, { childList: true, subtree: true });
    const onSubmit = async (e) => {
      const form = e.target.closest ? e.target.closest("form") : null;
      if (!form || !form.querySelector('input[name="source_article"]')) return;
      e.preventDefault();
      const data = Object.fromEntries(new FormData(form).entries());
      try {
        await axios.post(`${API}/lead-magnets/article-lead`, { ...data, hutk: getHutk(), ...utmPayload() });
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: "generate_lead", lead_type: "guide", content_source: `blog:${data.source_article || slug}` });
        navigate("/thank-you/lead");
      } catch {
        toast.error("Something went wrong — please try again.");
      }
    };
    container.addEventListener("submit", onSubmit);
    return () => {
      mo.disconnect();
      container.removeEventListener("submit", onSubmit);
    };
  }, [post, loading]);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: post.title, url }); } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const isHtml = (text) => /<[a-z][\s\S]*>/i.test(text);

  const headings = useMemo(() => {
    if (!post?.content) return [];
    return isHtml(post.content) ? extractHeadings(post.content) : extractMarkdownHeadings(post.content);
  }, [post]);

  const wordCount = useMemo(() => {
    if (!post?.content) return 0;
    const text = post.content.replace(/<[^>]*>/g, "");
    return text.split(/\s+/).filter(Boolean).length;
  }, [post]);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full animate-spin" />
          <span className="text-gray-500 text-sm">Loading article...</span>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <PageMeta title="Article Not Found | Digital Media Ninja" robots="noindex, nofollow" />
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Article not found</h1>
          <Button onClick={() => navigate("/blog")} className="btn-ninja">Back to Blog</Button>
        </div>
      </div>
    );
  }

  const { title, author, category, read_time, content, featured_image, slug: postSlug, tags = [], excerpt, faqs = [], internal_links = [], date_published, date } = post;
  const postDate = date_published || date;
  const allLinks = [...internal_links, ...SITE_LINKS];

  // Schema markup
  const articleSchema = {
    "@context": "https://schema.org", "@type": "Article",
    headline: title, description: excerpt, image: featured_image,
    author: { "@type": "Person", name: author, url: "https://digitalmedianinja.com/about" },
    publisher: { "@type": "Organization", name: "Digital Media Ninja", logo: { "@type": "ImageObject", url: "https://digitalmedianinja.com/logo.png" }, url: "https://digitalmedianinja.com" },
    datePublished: postDate, dateModified: postDate,
    mainEntityOfPage: { "@type": "WebPage", "@id": `https://digitalmedianinja.com/blog/${postSlug}` },
    articleSection: category, keywords: tags.join(", "),
    wordCount: content ? content.split(/\s+/).length : 0,
    isAccessibleForFree: true, inLanguage: "en-US"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, item: { "@id": "https://digitalmedianinja.com/", name: "Home" } },
      { "@type": "ListItem", position: 2, item: { "@id": "https://digitalmedianinja.com/blog/", name: "Blog" } },
      { "@type": "ListItem", position: 3, item: { "@id": `https://digitalmedianinja.com/blog/${postSlug}`, name: title } }
    ]
  };

  const faqSchema = faqs.length > 0 ? {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: faqs.map(faq => ({ "@type": "Question", name: faq.question, acceptedAnswer: { "@type": "Answer", text: faq.answer } }))
  } : null;

  const webPageSchema = {
    "@context": "https://schema.org", "@type": "WebPage",
    name: title, description: excerpt,
    url: `https://digitalmedianinja.com/blog/${postSlug}`,
    isPartOf: { "@type": "WebSite", name: "Digital Media Ninja", url: "https://digitalmedianinja.com" },
    about: { "@type": "Thing", name: category, description: `Expert content about ${category} from Digital Media Ninja, a Dallas-based digital marketing agency.` },
    speakable: { "@type": "SpeakableSpecification", cssSelector: ["article h1", "article h2", "article p"] }
  };

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="blog-detail-page">
      <ReadingProgress wordCount={wordCount} />
      <BackToTop />

      <Helmet>
        <title>{post.meta_title || `${title} | Digital Media Ninja Blog`}</title>
        <meta name="description" content={post.meta_description || excerpt} />
        <meta name="keywords" content={tags.join(", ")} />
        <link rel="canonical" href={`https://digitalmedianinja.com/blog/${postSlug}`} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={excerpt} />
        <meta property="og:image" content={featured_image || DEFAULT_IMAGE} />
        <meta property="og:url" content={`https://digitalmedianinja.com/blog/${postSlug}`} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={postDate} />
        <meta property="article:author" content={author} />
        <meta property="article:section" content={category} />
        {tags.map((tag, i) => <meta key={i} property="article:tag" content={tag} />)}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={excerpt} />
        <meta name="twitter:image" content={featured_image || DEFAULT_IMAGE} />
      </Helmet>

      {!post.schema_markup && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />}
      {!post.schema_markup && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />}
      {!post.schema_markup && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />}
      {faqSchema && !post.schema_markup && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />}
      {post.schema_markup && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(post.schema_markup) }} />}

      {/* Hero */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-[#00E676]/[0.03] to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6 pt-4" data-testid="breadcrumb">
            <Link to="/" className="hover:text-[#00E676] transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/blog" className="hover:text-[#00E676] transition-colors">Blog</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gray-600 truncate max-w-[200px]">{title}</span>
          </nav>

          <Button variant="ghost" className="mb-4 text-gray-500 hover:text-white -ml-2" onClick={() => navigate("/blog")} data-testid="back-to-blog-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
          </Button>

          {/* Category + Meta */}
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <Link to={`/blog/category/${category?.toLowerCase().replace(/\s+/g, '-')}`}>
              <span className="px-3 py-1 rounded-full bg-[#00E676]/10 text-[#00E676] text-xs font-semibold tracking-wide uppercase border border-[#00E676]/20">
                {category}
              </span>
            </Link>
            <span className="text-gray-600">|</span>
            <span className="text-gray-500 text-sm flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {read_time} min read</span>
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-[1.15] tracking-tight max-w-4xl" data-testid="article-title">
            {title}
          </h1>

          {/* Excerpt */}
          {excerpt && (
            <p className="text-lg text-gray-400 leading-relaxed max-w-3xl mb-6">{excerpt}</p>
          )}

          {/* Author + Date + Share */}
          <div className="flex flex-wrap items-center justify-between gap-4 pb-8 border-b border-white/[0.06]">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-[#00E676]/15 flex items-center justify-center">
                <User className="w-5 h-5 text-[#00E676]" />
              </div>
              <div>
                <Link to="/about/daniel-rivas" className="text-white text-sm font-medium hover:text-[#00E676] transition-colors" data-testid="blog-author-link">{author}</Link>
                {postDate && (
                  <p className="text-gray-500 text-xs">
                    {new Date(postDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                  </p>
                )}
              </div>
            </div>
            <Button variant="outline" size="sm" className="border-white/10 text-gray-400 hover:text-white hover:border-white/20" onClick={handleShare} data-testid="share-btn">
              {copied ? <Check className="w-4 h-4 mr-2 text-[#00E676]" /> : <Share2 className="w-4 h-4 mr-2" />}
              {copied ? "Copied!" : "Share"}
            </Button>
          </div>
        </div>
      </div>

      {/* Featured Image */}
      {featured_image && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
          <div className="relative rounded-2xl overflow-hidden aspect-[2/1]">
            <img
              src={featured_image}
              alt={title}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200"; }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
          </div>
        </div>
      )}

      {/* Content Area with ToC sidebar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        <div className="flex gap-12 items-start">
          {/* Main content */}
          <article className="flex-1 min-w-0 max-w-4xl" ref={contentRef}>
            <ArticleContent content={content} internalLinks={allLinks} />

            {/* Lead magnet: 100 Hooks swipe file */}
            {postSlug === "social-media-hooks-that-stop-the-scroll" && <SwipeFileGate />}

            {/* FAQ Section */}
            {faqs.length > 0 && (
              <div className="mt-16 pt-12 border-t border-white/[0.06]" data-testid="faq-section">
                <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#00E676]/15 flex items-center justify-center">
                    <HelpCircle className="w-4 h-4 text-[#00E676]" />
                  </div>
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  {faqs.map((faq, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} className="border border-white/[0.06] rounded-xl overflow-hidden bg-white/[0.02]">
                      <AccordionTrigger className="px-6 py-4 text-left hover:no-underline hover:bg-white/[0.03]">
                        <span className="text-white font-medium text-[15px]">{faq.question}</span>
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-5 text-gray-400 leading-relaxed">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}

            {/* Tags */}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-12">
                {tags.map((tag, i) => (
                  <span key={i} className="px-3 py-1.5 rounded-full bg-white/[0.04] text-gray-500 text-xs border border-white/[0.06] hover:bg-white/[0.08] transition-colors cursor-default">
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Author Box */}
            <div className="mt-12 bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6 sm:p-8">
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 rounded-xl bg-[#00E676]/10 flex items-center justify-center shrink-0">
                  <User className="w-7 h-7 text-[#00E676]" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Written by</p>
                  <h3 className="text-lg font-bold mb-2"><Link to="/about/daniel-rivas" className="hover:text-[#00E676] transition-colors" data-testid="blog-author-bio-link">{author}</Link></h3>
                  <p className="text-gray-400 text-sm leading-relaxed mb-4">
                    Founder of Digital Media Ninja. Every article on this site is written or reviewed by Daniel —
                    covering local SEO, AEO/GEO, and AI-powered marketing for Dallas–Fort Worth businesses.
                  </p>
                  <Link to="/contact">
                    <Button size="sm" className="btn-ninja">Get in Touch</Button>
                  </Link>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="mt-12 bg-gradient-to-br from-[#00E676]/[0.08] to-[#6366F1]/[0.08] border border-white/[0.08] rounded-2xl p-8 text-center">
              <BookOpen className="w-8 h-8 text-[#00E676] mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-3">Ready to Grow Your Business?</h2>
              <p className="text-gray-400 mb-6 max-w-xl mx-auto text-sm leading-relaxed">
                Get a free digital marketing audit and discover how we can help your business dominate search results.
              </p>
              <Link to="/contact">
                <Button className="btn-ninja px-8">Get Your Free Audit</Button>
              </Link>
            </div>
          </article>

          {/* Table of Contents sidebar */}
          <TableOfContents headings={headings} activeId={activeHeading} />
        </div>
      </div>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
          <RelatedPosts posts={relatedPosts} />
        </div>
      )}

      {/* Texas Coverage Widget */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
        <TexasCoverageWidget variant="full" />
      </div>
    </div>
  );
};

// Enhanced article content renderer
const ArticleContent = ({ content, internalLinks = [] }) => {
  const processInternalLinks = (text) => {
    if (!internalLinks || internalLinks.length === 0) return text;
    let processed = text;
    const usedUrls = new Set();
    internalLinks.forEach((link) => {
      if (usedUrls.has(link.url)) return;
      const escaped = link.text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`(?<![/\\w])\\b(${escaped})\\b(?!["<])`, "i");
      if (regex.test(processed) && !processed.includes(`href="${link.url}"`)) {
        processed = processed.replace(regex,
          `<a href="${link.url}" class="text-[#00E676] hover:text-[#00C853] underline decoration-[#00E676]/30 underline-offset-2 hover:decoration-[#00E676]/60 transition-colors">$1</a>`
        );
        usedUrls.add(link.url);
      }
    });
    return processed;
  };

  const addIdsToHeadings = (html) => {
    return html.replace(/<h([23])([^>]*)>(.*?)<\/h([23])>/gi, (match, level, attrs, inner, closeLevel) => {
      const text = inner.replace(/<[^>]*>/g, "").trim();
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
      if (attrs.includes("id=")) return match;
      return `<h${level} id="${id}"${attrs}>${inner}</h${level}>`;
    });
  };

  const isHtmlContent = /<[a-z][\s\S]*>/i.test(content);

  if (isHtmlContent) {
    let processed = addIdsToHeadings(content);
    processed = processInternalLinks(processed);

    // Enhanced HTML styling
    processed = processed
      .replace(/<h2([^>]*)>/g, '<h2$1 class="text-2xl sm:text-3xl font-bold mt-14 mb-5 text-white leading-tight scroll-mt-24">')
      .replace(/<h3([^>]*)>/g, '<h3$1 class="text-xl sm:text-2xl font-semibold mt-10 mb-4 text-white leading-tight scroll-mt-24">')
      .replace(/<h4([^>]*)>/g, '<h4$1 class="text-lg font-semibold mt-8 mb-3 text-white">')
      .replace(/<p>/g, '<p class="text-gray-300 mb-5 leading-[1.8] text-[15px]">')
      .replace(/<ul>/g, '<ul class="list-none space-y-3 mb-6 text-gray-300">')
      .replace(/<ol>/g, '<ol class="list-decimal space-y-3 mb-6 text-gray-300 pl-5 marker:text-[#00E676]">')
      .replace(/<li>/g, '<li class="text-gray-300 leading-relaxed text-[15px] pl-1">')
      .replace(/<strong>/g, '<strong class="text-white font-semibold">')
      .replace(/<blockquote>/g, '<blockquote class="border-l-2 border-[#00E676] pl-5 my-6 italic text-gray-400">')
      .replace(/<a (?![^>]*class=)/g, '<a class="text-[#00E676] hover:text-[#00C853] underline decoration-[#00E676]/30 underline-offset-2 hover:decoration-[#00E676]/60 transition-colors" ')
      .replace(/<table>/g, '<div class="overflow-x-auto mb-8 rounded-xl border border-white/[0.06]"><table class="w-full text-sm text-left">')
      .replace(/<\/table>/g, '</table></div>')
      .replace(/<thead>/g, '<thead class="bg-white/[0.05] text-white">')
      .replace(/<th>/g, '<th class="px-4 py-3 font-semibold text-white text-sm border-b border-white/[0.06]">')
      .replace(/<td>/g, '<td class="px-4 py-3 text-gray-300 text-sm border-b border-white/[0.03]">')
      .replace(/<img (?![^>]*class=)/g, '<img loading="lazy" decoding="async" class="rounded-xl border border-white/[0.08] my-4 w-full h-auto" ')
      .replace(/<tr>/g, '<tr class="hover:bg-white/[0.02] transition-colors">');

    // Add bullet style to unordered list items that don't have custom markers
    processed = processed.replace(
      /<li class="text-gray-300 leading-relaxed text-\[15px\] pl-1">(?!<span class="li-marker")/g,
      '<li class="text-gray-300 leading-relaxed text-[15px] pl-1 relative"><span class="li-marker text-[#00E676] mr-2 font-bold inline-block w-2">&#8226;</span>'
    );

    return (
      <div className="article-content" dangerouslySetInnerHTML={{ __html: processed }} />
    );
  }

  // Markdown rendering
  const lines = content.split("\n");
  const elements = [];
  let currentList = [];
  let listType = null;
  let idx = 0;

  const flushList = () => {
    if (currentList.length === 0) return;
    idx++;
    if (listType === "ul") {
      elements.push(
        <ul key={`list-${idx}`} className="list-none space-y-3 mb-6 text-gray-300">
          {currentList.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-[15px] leading-relaxed">
              <span className="text-[#00E676] mt-1 font-bold shrink-0">&#8226;</span>
              <span dangerouslySetInnerHTML={{ __html: processInternalLinks(formatBold(item)) }} />
            </li>
          ))}
        </ul>
      );
    } else {
      elements.push(
        <ol key={`list-${idx}`} className="list-decimal space-y-3 mb-6 text-gray-300 pl-5 marker:text-[#00E676]">
          {currentList.map((item, i) => (
            <li key={i} className="text-[15px] leading-relaxed pl-1">
              <span dangerouslySetInnerHTML={{ __html: processInternalLinks(formatBold(item)) }} />
            </li>
          ))}
        </ol>
      );
    }
    currentList = [];
    listType = null;
  };

  const formatBold = (text) => text.replace(/\*\*([^*]+)\*\*/g, '<strong class="text-white font-semibold">$1</strong>');

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) { flushList(); return; }

    if (trimmed.startsWith("## ") && !trimmed.startsWith("### ")) {
      flushList(); idx++;
      const text = trimmed.replace("## ", "");
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
      elements.push(<h2 key={`h2-${idx}`} id={id} className="text-2xl sm:text-3xl font-bold mt-14 mb-5 text-white leading-tight scroll-mt-24">{text}</h2>);
    } else if (trimmed.startsWith("### ")) {
      flushList(); idx++;
      const text = trimmed.replace("### ", "");
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
      elements.push(<h3 key={`h3-${idx}`} id={id} className="text-xl sm:text-2xl font-semibold mt-10 mb-4 text-white leading-tight scroll-mt-24">{text}</h3>);
    } else if (trimmed.startsWith("**") && trimmed.endsWith("**")) {
      flushList(); idx++;
      elements.push(<h4 key={`h4-${idx}`} className="text-lg font-semibold mt-8 mb-3 text-white">{trimmed.replace(/\*\*/g, "")}</h4>);
    } else if (trimmed.startsWith("- ")) {
      if (listType !== "ul") flushList();
      listType = "ul";
      currentList.push(trimmed.replace("- ", ""));
    } else if (/^\d+\.\s/.test(trimmed)) {
      if (listType !== "ol") flushList();
      listType = "ol";
      currentList.push(trimmed.replace(/^\d+\.\s/, ""));
    } else if (trimmed.startsWith("*") && trimmed.endsWith("*") && !trimmed.startsWith("**")) {
      flushList(); idx++;
      elements.push(<p key={`em-${idx}`} className="text-[#00E676]/80 italic mb-4 text-sm">{trimmed.replace(/^\*|\*$/g, "")}</p>);
    } else {
      flushList(); idx++;
      elements.push(
        <p key={`p-${idx}`} className="text-gray-300 mb-5 leading-[1.8] text-[15px]" dangerouslySetInnerHTML={{ __html: processInternalLinks(formatBold(trimmed)) }} />
      );
    }
  });
  flushList();

  return <div className="article-content">{elements}</div>;
};

// Related posts
const RelatedPosts = ({ posts }) => (
  <div data-testid="related-posts">
    <h2 className="text-2xl font-bold mb-8">Related Articles</h2>
    <div className="grid md:grid-cols-3 gap-6">
      {posts.map((p) => (
        <Link key={p.slug || p.id} to={`/blog/${p.slug}`} className="group">
          <Card className="bg-white/[0.02] border-white/[0.06] hover:border-[#00E676]/20 transition-all duration-300 overflow-hidden h-full">
            <div className="relative h-44 overflow-hidden">
              <img
                src={p.featured_image}
                alt={p.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800"; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-2.5">
                <Clock className="w-3.5 h-3.5" />
                <span>{p.read_time} min read</span>
              </div>
              <h3 className="text-sm font-semibold leading-snug group-hover:text-[#00E676] transition-colors line-clamp-2">
                {p.title}
              </h3>
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  </div>
);

export default BlogDetailPage;
